import React from "react";
import { Link } from "react-router-dom";
import { HERO_IMAGE, COMPANY } from "@/data/departments";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { useSite, useUrl } from "@/context/SiteContext";
import { useDepartments } from "@/context/DepartmentsContext";
import { motion } from "framer-motion";
import { MaskedLines, Reveal, ParallaxImage } from "@/components/motion";
import { SmartLink } from "@/components/SmartLink";

export default function HomePage() {
  const { t, list } = useSite();
  const url = useUrl();
  const { departments } = useDepartments();
  const heroImage = t("home.hero.image_url", HERO_IMAGE);
  const items = departments || [];
  const stats = list("home.stats");
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative border-b border-black" data-testid="hero-section">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 pt-14 md:pt-20 pb-10">
          <div className="font-mono-label text-black/60 flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-black"></span>
            <span>{t("home.hero.eyebrow")}</span>
          </div>

          <h1 className="mt-6 font-display font-medium uppercase tracking-tighter leading-[0.9] text-5xl sm:text-7xl lg:text-[7.5rem]">
            <MaskedLines
              lines={[t("home.hero.title_1"), t("home.hero.title_2"), t("home.hero.title_3")]}
              italicIndexes={[1]}
              delay={0.2}
            />
          </h1>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-8">
            <p className="md:col-span-6 text-base text-black/70 leading-relaxed">
              {t("home.hero.description")}
            </p>
            <div className="md:col-span-6 flex md:justify-end items-end gap-4 flex-wrap">
              <SmartLink to={t("btn.home.hero.primary.link", url.contact)} data-testid="hero-cta-contact" className="font-mono-label bg-black text-white px-6 py-4 hover:bg-white hover:text-black border border-black inline-flex items-center gap-2">
                {t("btn.home.hero.primary.label", t("home.hero.cta_primary"))} <ArrowRight size={16} />
              </SmartLink>
              <SmartLink to={t("btn.home.hero.secondary.link", url.departments)} data-testid="hero-cta-departments" className="font-mono-label px-6 py-4 border border-black hover:bg-black hover:text-white">
                {t("btn.home.hero.secondary.label", t("home.hero.cta_secondary"))}
              </SmartLink>
            </div>
          </div>
        </div>

        {/* Hero image with parallax */}
        <div className="relative h-[45vh] md:h-[65vh] border-t border-black overflow-hidden">
          <ParallaxImage src={heroImage} alt="Suntec industrial" />
          <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10 text-white mix-blend-difference font-mono-label z-10">
            [ 01 / SCARĂ INDUSTRIALĂ · 20+ ANI ]
          </div>
        </div>
      </section>

      {/* EU Projects Hero */}
      <section className="border-b border-black bg-black text-white" data-testid="eu-projects-section">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="font-mono-label text-white/50">{t("section.home.eu.eyebrow")}</div>
            <h2 className="mt-4 font-display font-medium uppercase text-4xl md:text-6xl tracking-tighter leading-[0.95]">
              {t("home.eu.title_1")} <span className="italic font-normal">{t("home.eu.title_2")}</span>
            </h2>
            <p className="mt-6 text-white/70 text-base leading-relaxed max-w-2xl">
              {t("home.eu.description")}
            </p>
            <SmartLink to={t("btn.home.eu.link", url.dept("proiecte-europene"))} data-testid="eu-projects-cta" className="mt-8 inline-flex items-center gap-3 font-mono-label bg-white text-black px-6 py-4 hover:bg-black hover:text-white border border-white">
              {t("btn.home.eu.label")} <ArrowRight size={16} />
            </SmartLink>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-px bg-white/20 border border-white/20 self-start">
            <div className="bg-black p-6 md:p-8">
              <div className="font-display font-medium text-5xl md:text-7xl tracking-tighter">{t("home.eu.stat1_num")}</div>
              <div className="font-mono-label mt-3 text-white/60">{t("home.eu.stat1_label")}</div>
            </div>
            <div className="bg-black p-6 md:p-8">
              <div className="font-display font-medium text-5xl md:text-7xl tracking-tighter">{t("home.eu.stat2_num")}</div>
              <div className="font-mono-label mt-3 text-white/60">{t("home.eu.stat2_label")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Departments grid */}
      <section className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-16 md:py-24" data-testid="departments-section">
        <Reveal>
          <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
            <div>
              <div className="font-mono-label text-black/60">{t("section.home.departments.eyebrow")}</div>
              <h2 className="mt-3 font-display font-medium uppercase text-4xl md:text-6xl tracking-tighter">
                {items.length || "Nouă"} {t("section.home.departments.title_1")}<br />{t("section.home.departments.title_2")}
              </h2>
            </div>
            <SmartLink to={t("btn.home.departments_all.link", url.departments)} className="font-mono-label underline underline-offset-4" data-testid="see-all-departments">
              {t("btn.home.departments_all.label")}
            </SmartLink>
          </div>
        </Reveal>

        {items.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-black border border-black">
            {items.map((d, i) => (
              <motion.div
                key={d.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.7, delay: (i % 3) * 0.06, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link
                  to={url.dept(d.slug)}
                  data-testid={`department-card-${d.slug}`}
                  className="group bg-white p-6 md:p-8 hover:bg-black hover:text-white transition-all duration-300 flex flex-col justify-between min-h-[280px] h-full"
                >
                  <div className="flex items-start justify-between">
                    <div className="font-mono-label opacity-60">{d.number}</div>
                    <ArrowUpRight
                      size={20}
                      className="opacity-40 group-hover:opacity-100 group-hover:rotate-45 transition-all duration-500"
                    />
                  </div>
                  <div>
                    <div className="font-display font-medium uppercase text-2xl md:text-3xl tracking-tight leading-none">
                      {d.name}
                    </div>
                    <div className="mt-3 text-sm opacity-70 leading-relaxed">{d.tagline}</div>
                  </div>
                </Link>
              </motion.div>
            ))}
            {/* Filler cells to prevent black gaps when count isn't a multiple of 3 (lg) or 2 (sm) */}
            {Array.from({ length: (3 - (items.length % 3)) % 3 }).map((_, i) => (
              <div key={`fill-${i}`} aria-hidden className="hidden lg:block bg-white" data-testid={`grid-filler-${i}`} />
            ))}
            {items.length % 2 !== 0 && (
              <div aria-hidden className="hidden sm:block lg:hidden bg-white" />
            )}
          </div>
        ) : (
          <div className="border border-black p-12 text-center font-mono-label text-black/60">
            Se încarcă departamentele...
          </div>
        )}
      </section>

      {/* Stats */}
      <section className="border-y border-black bg-white" data-testid="stats-section">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-16 grid grid-cols-2 lg:grid-cols-4 gap-px bg-black border-x border-black">
          {stats.map((s, i) => (
            <div key={i} className="bg-white p-8 md:p-10">
              <div className="font-display font-medium text-5xl md:text-7xl tracking-tighter">{s.k}</div>
              <div className="font-mono-label mt-3 text-black/60">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-24" data-testid="cta-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="font-mono-label text-black/60">{t("section.home.cta.eyebrow")}</div>
            <h2 className="mt-3 font-display font-medium uppercase text-5xl md:text-7xl tracking-tighter leading-[0.9]">
              {t("home.cta.title_1")}<br />
              <span className="italic font-normal">{t("home.cta.title_2")}</span> {t("home.cta.title_3")}
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <SmartLink to={t("btn.home.cta.link", url.contact)} data-testid="cta-contact-btn" className="font-mono-label bg-black text-white px-8 py-5 hover:bg-white hover:text-black border border-black inline-flex items-center gap-3">
              {t("btn.home.cta.label")} <ArrowRight size={16} />
            </SmartLink>
          </div>
        </div>
      </section>
    </div>
  );
}
